// FRONT-765 (live-test finding, 2026-09-02): whether the composer should
// treat the current turn as busy (showing Stop, blocking a free send).
//
// A round already mid-flight (e.g. two parallel delegate_to_agent calls) has
// no server-side abort — the backend's cooperative cancel only takes effect
// at the NEXT round boundary, which can be tens of seconds to a minute away.
// Before this, both the visible Stop/Send state and the queued-draft gate
// waited on either `chat.status` settling or the polled background_chats
// row leaving `running` — both of which lag the user's Stop press by exactly
// that abandoned-round duration, so Stop looked inert: the button stayed on
// "Stop", clickable, doing nothing, until the round finished on its own.
//
// `manuallyStopped` (use-agent-chat.ts) is decided the instant the user
// clicks — these two functions fold it in so the composer frees immediately
// regardless of how long the network takes to catch up. Kept pure so the
// override is unit-testable without mounting the hook or a real Chat
// instance (the same reason resumeTargetToSuppress / decideQueuedSend /
// shouldReseedFromHistory are extracted this way in this file's siblings).

/** Minimal shape of the AI SDK's own chat status this needs. */
export type ChatRunStatus = 'submitted' | 'streaming' | 'ready' | 'error';

/**
 * Is the local SDK stream actively running? True only while genuinely
 * streaming/submitted AND the user hasn't deliberately stopped this turn.
 */
export function isTurnStreaming(status: ChatRunStatus, manuallyStopped: boolean): boolean {
  return (status === 'streaming' || status === 'submitted') && !manuallyStopped;
}

/**
 * Should the composer (queued-draft gate, "still busy" indicators) treat the
 * turn as busy? True while the local stream is active, OR while the polled
 * background_chats row still reads `running` — UNLESS the user has
 * deliberately stopped this turn, in which case a still-`running` row (the
 * abandoned round, not yet caught up to `cancelled`) no longer counts.
 */
export function isQueueBusy(
  isStreaming: boolean,
  bgRunStatus: string | null | undefined,
  manuallyStopped: boolean,
): boolean {
  return isStreaming || (bgRunStatus === 'running' && !manuallyStopped);
}
